import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Size, t, type PageSettingsEntry } from '@bms2/shared';
import { IconButton } from '../../components/controls';
import { useHangingList, useSettingsEntries, type SettingsEntryLink } from './SettingsRail';
import styles from './settings.module.css';

/**
 * 🔒 The gear on a page a setting governs (decision 2805; T1614). One gear at
 * the right end of the page's title bar, after the banner's one action (decision
 * 2815, amending 2805's place in the toolbar row), named to a reader and
 * in its tooltip (decision 1453), opens a menu of the entries that govern the
 * page, each a link to where it is edited. A page with one entry draws the same
 * gear and a menu of one, so the control reads the same everywhere.
 *
 * 🔨 The menu is the folded rail's own list and panel, hung through the same
 * hook, never a third construction.
 */
export function SettingsGear({ links }: { links: readonly SettingsEntryLink[] }) {
  const { open, toggle, close, anchor, panel, style } = useHangingList<HTMLSpanElement>('end');
  if (links.length === 0) return null;
  return (
    <span ref={anchor} className={styles.gearAnchor} data-page-settings="true">
      <IconButton meaning="settings" label={t('shell.pageSettings')} size={Size.Small} tone="onFill" onClick={toggle} name="page-settings" menuOpen={open} />
      {open && typeof document !== 'undefined'
        ? createPortal(
            <nav ref={panel} className={styles.kindMenuPanel} style={style} aria-label={t('shell.pageSettings')} data-page-settings-panel="true">
              <ul className={styles.railList}>
                {links.map((link) => (
                  <li key={link.route}>
                    <Link to={link.route} className={`${styles.railEntry} ${styles.railLink}`} data-page-settings-entry={link.route} onClick={() => close(false)}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>,
            document.body,
          )
        : null}
    </span>
  );
}

/**
 * The page's gear for its title bar, or null where this person may open none of
 * its entries, so the page hands the Page Module nothing and draws no gear.
 * 🔨 A page rendered outside the frame's Index holder gets no gear and no read.
 */
export function useSettingsGear(entries: readonly PageSettingsEntry[] | undefined): ReactNode | null {
  const links = useSettingsEntries(entries);
  return links.length === 0 ? null : <SettingsGear links={links} />;
}
